import VersionDisplay from '@/components/VersionDisplay';
import { INFO_SECTIONS, type InfoSectionId } from '@/content/lobbyInfoSections';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';

type InfoPageProps = {
  sectionId: InfoSectionId;
};

const InfoPage = ({ sectionId }: InfoPageProps) => {
  const navigate = useSafeNavigate();
  const section = INFO_SECTIONS[sectionId];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        boxSizing: 'border-box',
        padding: '2rem 1.5rem 3rem',
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => navigate('/teacher')}
        style={{
          position: 'absolute',
          top: '1.5rem',
          left: '1.5rem',
          padding: '0.8rem 1.5rem',
          fontSize: '1rem',
          backgroundColor: '#2196F3',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontWeight: 'bold',
          boxShadow: '0 4px 16px rgba(33,150,243,0.4)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          zIndex: 10,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(33,150,243,0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(33,150,243,0.4)';
        }}
      >
        👩‍🏫 Teacher Dash
      </button>

      <div
        style={{
          width: '100%',
          maxWidth: '960px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1.5rem',
          paddingTop: '4.5rem',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <VersionDisplay variant="transparent" />

        <div
          style={{
            width: '100%',
            background: 'rgba(12, 19, 70, 0.38)',
            border: `1px solid ${section.accent}55`,
            borderRadius: '28px',
            padding: '2rem',
            backdropFilter: 'blur(14px)',
            boxShadow: '0 18px 45px rgba(8, 14, 48, 0.28)',
            textAlign: 'left',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.45rem 0.85rem',
              borderRadius: '999px',
              background: 'rgba(255,255,255,0.08)',
              color: section.accent,
              fontSize: '0.9rem',
              fontWeight: 700,
              marginBottom: '1rem',
            }}
          >
            {section.label}
          </div>

          <h1
            style={{
              fontSize: 'clamp(2rem, 3.6vw, 3rem)',
              marginBottom: '1rem',
              color: 'white',
            }}
          >
            {section.title}
          </h1>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              fontSize: '1rem',
              lineHeight: 1.7,
              color: 'rgba(255,255,255,0.92)',
            }}
          >
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}

            {section.bullets && (
              <div
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  borderRadius: '18px',
                  padding: '1rem 1.1rem',
                }}
              >
                {section.bullets.map((bullet, index) => (
                  <p
                    key={bullet}
                    style={{
                      marginBottom:
                        index === section.bullets!.length - 1 ? 0 : '0.65rem',
                    }}
                  >
                    • {bullet}
                  </p>
                ))}
              </div>
            )}

            {section.email && (
              <p>
                Contact:{' '}
                <a
                  href={`mailto:${section.email}`}
                  style={{
                    color: section.accent,
                    fontWeight: 700,
                    textDecoration: 'none',
                  }}
                >
                  {section.email}
                </a>
              </p>
            )}
          </div>
        </div>

        <button
          onClick={() => navigate('/lobby')}
          style={{
            padding: '0.95rem 1.9rem',
            fontSize: '1rem',
            backgroundColor: 'rgba(255,255,255,0.16)',
            color: 'white',
            border: '2px solid rgba(255,255,255,0.35)',
            borderRadius: '999px',
            cursor: 'pointer',
            fontWeight: 'bold',
            boxShadow: '0 6px 18px rgba(0,0,0,0.2)',
            backdropFilter: 'blur(10px)',
            transition:
              'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 10px 22px rgba(0,0,0,0.28)';
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.22)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.2)';
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.16)';
          }}
        >
          Back to Lobby
        </button>
      </div>

      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -1,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          overflow: 'hidden',
        }}
      >
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${3 + Math.random() * 4}s ease-in-out infinite ${
                Math.random() * 2
              }s`,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.7; }
          50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default InfoPage;
